import { Injectable } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { GameTabManager } from "./game-tab-manager.interface";

interface GameTabMessage {
    type: 'OPEN_OR_FOCUS_TABLE' | 'CLOSE_TABLE';
    gameTabName: string;
    oldGameTabName?: string;
}

@Injectable({ providedIn: "root" })
export class BrowserGameTabManager extends GameTabManager{
    private tabs = new Map<string, Window>();
    private channel: BroadcastChannel;
    private isLobby = false;
    private heartbeatInterval: ReturnType<typeof setInterval>;

    constructor(
        private toastController: ToastController,
        private sopoPopupService: SopoPopupService,
    ) {
        super();
        this.channel = new BroadcastChannel("gameTab-manager");
        this.channel.onmessage = (ev) => this.process(ev.data);

        this.tryBecomeLobby();

        window.addEventListener("beforeunload", (event) => {
            if (this.isLobby) {
                event.preventDefault();
                event.returnValue = "";
            }
        });
        window.addEventListener("unload", () => {
            if (this.isLobby) clearInterval(this.heartbeatInterval);
        });
    }

    private tryBecomeLobby() {
        if (this.isLobby) return true;

        const now = Date.now();
        const lastPing = Number(localStorage.getItem("lobbyHeartbeat") || "0");
        const heartbeatDead = now - lastPing > 3000;

        if (!heartbeatDead) return false;

        if (window.location.pathname.includes("/coinflow-checkout")) {
            return false;
        }

        if (!window.location.pathname.includes("/game")) {
            this.isLobby = true;
            this.startHeartbeat();
            return true;
        }
        setTimeout(() => {
            // game tab must NOT become lobby immediately
            const now2 = Date.now();
            const lastPing2 = Number(
                localStorage.getItem("lobbyHeartbeat") || "0",
            );
            const heartbeatDead2 = now2 - lastPing2 > 3000;

            if (heartbeatDead2 && !this.isLobby) {
                this.isLobby = true;
                this.startHeartbeat();
            }
        }, 500);
        return false;
    }

    private startHeartbeat() {
        localStorage.setItem("lobbyHeartbeat", Date.now().toString());
        this.heartbeatInterval = setInterval(() => {
            localStorage.setItem("lobbyHeartbeat", Date.now().toString());
        }, 2000);
    }

    private process(msg: GameTabMessage) {
        if (!this.isLobby && !this.tryBecomeLobby()) {
            if (!location.pathname.includes("/game")) {
                this.sopoPopupService.showAlert("app", {
                    header: "You already have a lobby opened.",
                    message:
                        "Please only use the old lobby to manage your game tables.",
                    dismissAfter: 1000,
                });
            }
            return;
        }

        if (window.name === msg.gameTabName && this.isLobby) {
            window.name = "lobby";
            this.tabs.delete(msg.gameTabName);
        }

        switch (msg.type) {
            case "OPEN_OR_FOCUS_TABLE":
                this.openOrFocus(msg.gameTabName, msg.oldGameTabName);
                break;

            case "CLOSE_TABLE":
                this.close(msg.gameTabName);
                break;
        }
    }

    openOrFocus(gameTabName: string, oldGameTabName?: string): Promise<void> {
        if (gameTabName === oldGameTabName) return Promise.resolve();
        const tab = this.openTab(gameTabName);

        if (oldGameTabName && oldGameTabName !== gameTabName) {
            this.close(oldGameTabName);
        }

        if (!tab) return Promise.resolve();

        try {
            tab.focus();
            return this.waitForTabOpened(gameTabName);
        } catch (err) {
            console.warn("Cannot focus tab:", err);
            return Promise.resolve();
        }
    }

    private waitForTabOpened(gameTabName: string): Promise<void> {
        return new Promise((resolve) => {
            const channel = new BroadcastChannel('gameTab-manager');
            const timeout = setTimeout(() => {
                channel.close();
                resolve();
            }, 3000);
            channel.onmessage = (event) => {
                if (event.data.type === 'TAB_OPENED' && event.data.gameTabName === gameTabName) {
                    clearTimeout(timeout);
                    channel.close();
                    resolve();
                }
            };
        });
    }

    private openTab(gameTabName: string) {
        let tab = this.tabs.get(gameTabName);

        if (!tab || tab.closed) {
            tab = window.open("", gameTabName);
            if (!tab) {
                this.showPopupBlockedToast(gameTabName);
                return;
            }
            tab.location.href = `/game`;
            this.tabs.set(gameTabName, tab);
        }
        return tab;
    }

    close(gameTabName: string) {
        const tab = this.tabs.get(gameTabName);
        if (tab && !tab.closed) tab.close();
        this.tabs.delete(gameTabName);
    }

    private async showPopupBlockedToast(gameTabName: string) {
        const toast = await this.toastController.create({
            message:
                "Pop-ups are blocked. Please allow pop-ups for this website for table rebalancing. Click here to return to your table.",
            duration: 15000,
            position: "bottom",
            color: "warning",
            buttons: [
                {
                    text: "Return to Table",
                    role: "cancel",
                    handler: () => {
                        this.openOrFocus(gameTabName);
                    },
                },
            ],
        });
        toast.present();
    }
}
